/**
 * Live updates for the inbox.
 *
 * One EventSource per mounted inbox, re-opened when the agent switches
 * conversation so the stream carries that conversation's messages too. The
 * callbacks are held in a ref: a parent that re-renders on every keystroke would
 * otherwise tear the connection down and build it again.
 *
 * The stream credential is short-lived by design, so reconnecting means minting
 * a new one. EventSource's own retry would go back to the same URL with the same
 * expired token forever, and the inbox would stop updating with nothing to show
 * for it — so the error path closes the connection and starts over.
 */

import { useEffect, useRef } from 'react';

import { fetchStreamToken, SUPPORT_URL } from './api';
import { InboxEvent, Message } from './types';

type Handlers = {
  onMessage: (message: Message) => void;
  onInboxChanged: (event: InboxEvent) => void;
};

const FIRST_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;

const useSupportStream = (conversationId: string | null, handlers: Handlers): void => {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!SUPPORT_URL) return undefined;

    let source: EventSource | null = null;
    let retryTimer: number | undefined;
    let retryMs = FIRST_RETRY_MS;
    let cancelled = false;

    const connect = async (): Promise<void> => {
      const retryLater = (): void => {
        if (cancelled) return;
        retryTimer = window.setTimeout(() => {
          void connect();
        }, retryMs);
        retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
      };

      let token: string;
      try {
        ({
          data: { token },
        } = await fetchStreamToken());
      } catch {
        // The inbox still works without the stream; keep trying quietly.
        retryLater();
        return;
      }
      if (cancelled) return;

      const params = new URLSearchParams({ t: token });
      if (conversationId) params.set('conversation_id', conversationId);
      source = new EventSource(`${SUPPORT_URL}/api/agent/stream?${params.toString()}`);

      source.addEventListener('open', () => {
        retryMs = FIRST_RETRY_MS;
      });

      source.addEventListener('message', (event) => {
        try {
          handlersRef.current.onMessage(JSON.parse(event.data) as Message);
        } catch {
          // One malformed frame must not kill the listener for the rest.
        }
      });

      ['conversation.new', 'conversation.updated'].forEach((name) => {
        source?.addEventListener(name, (event) => {
          let data: InboxEvent = {};
          try {
            // JSON.parse("null") doesn't throw, and a thrown TypeError in this
            // listener would silently stop the list from refreshing.
            const parsed: unknown = JSON.parse((event as MessageEvent).data);
            if (parsed && typeof parsed === 'object') data = parsed as InboxEvent;
          } catch {
            // A frame with no readable payload still means the list is stale.
          }
          handlersRef.current.onInboxChanged(data);
        });
      });

      source.addEventListener('error', () => {
        // Do not let EventSource retry on its own: its URL carries a token that
        // may be exactly what the server just rejected.
        source?.close();
        source = null;
        retryLater();
      });
    };

    void connect();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      source?.close();
    };
  }, [conversationId]);
};

export default useSupportStream;
